/**
 * Helpers for interpreting a chronos_review_state row returned by
 * GET /review-status/{id}.
 *
 * The central rule: a review-state row EXISTING tells you nothing about whether
 * a validation is running. chronos_review_state retains its row forever once an
 * FR has been submitted, including after it reaches a terminal state, so every
 * FR that has ever run has one. Only the review_state VALUE says whether a run
 * is in flight.
 *
 * Getting this wrong is not cosmetic. The backend's authoritative guard —
 * invoke_validation's conditional write — permits a re-queue on exactly the
 * terminal states:
 *
 *     attribute_not_exists(FR_id)
 *       OR review_state = '3-Completed'
 *       OR review_state = '4-Failed'
 *
 * so any client-side check that blocks on existence alone is stricter than the
 * backend contract and can refuse a re-run the backend would have accepted.
 */

/**
 * The non-terminal states: '1-Pending' is written by invoke_validation when it
 * queues the FR, '2-In Progress' by stream_processor when it claims the row and
 * invokes the agent. Everything else — '3-Completed', '4-Failed', or an absent
 * or unrecognised value — is NOT in flight.
 */
export const IN_FLIGHT_REVIEW_STATES = Object.freeze(['1-Pending', '2-In Progress']);

/**
 * Pull review_state out of a review-status response, which comes in two shapes:
 * the single-item path returns the row directly, the list path nests it under
 * fundRequests[]. Mirrors the accessor already used at the polling call sites.
 *
 * @param {object} statusData - parsed GET /review-status response
 * @returns {string|undefined} the review_state value, or undefined if absent
 */
export const extractReviewState = (statusData) =>
  statusData?.review_state || statusData?.fundRequests?.[0]?.review_state;

/**
 * Is a validation currently running for this FR?
 *
 * Returns false for terminal states and for any state this does not recognise.
 * That is deliberate: an unknown value must not deadlock the UI, and the
 * backend's conditional write remains the real guard — it answers a genuinely
 * in-flight FR with a 409 that the caller surfaces.
 *
 * @param {object} statusData - parsed GET /review-status response
 * @returns {boolean}
 */
export const isValidationInFlight = (statusData) =>
  IN_FLIGHT_REVIEW_STATES.includes(extractReviewState(statusData));
